import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import { TransactionConversion } from '@/models/transaction';
import { createBEMClasses } from '@/utils/classname';
import { Currency, formatCurrency } from '@/utils/number';

import { ConversionTooltip } from '../PriceWithDetails/PriceWithDetails';

import '../PriceWithDetails/PriceWithDetails.css';

const { element } = createBEMClasses('price-with-details');

type TransactionPriceAmountWithConversions = {
  amount: number;
  currency: Currency;
  conversions: TransactionConversion[];
};

type TransactionPriceLine = {
  label: string;
  amount: number;
  currency: Currency;
  conversions: TransactionConversion[];
};

export const TransactionPriceWithDetailsTooltipWithConversions = ({
  price,
  lines,
  size = 'medium',
  variant = 'primary',
  direction = 'row',
  tooltipDirection = 'row',
  gap = '0.5rem',
}: {
  price: TransactionPriceAmountWithConversions;
  lines: TransactionPriceLine[];
  size?: 'x-small' | 'small' | 'medium';
  variant?: 'primary' | 'secondary' | 'warning';
  direction?: 'row' | 'row-reverse';
  tooltipDirection?: 'row' | 'row-reverse';
  gap?: string;
}) => {
  return (
    <Stack
      style={{
        flexDirection: direction,
        display: 'inline-flex',
        flexWrap: 'nowrap',
      }}
      gap={gap}
    >
      <TransactionPriceTooltip price={price} lines={lines} size={size} variant={variant} />
      {price.conversions.map((priceConversion) => {
        const currency = priceConversion.currency;
        const convertedLines = lines
          .map((line): TransactionPriceLine | null => {
            const lineConversion = line.conversions.find((c) => c.currency === currency);

            if (!lineConversion) {
              return null;
            }

            return { label: line.label, amount: lineConversion.amount, currency, conversions: [] };
          })
          .filter((line): line is TransactionPriceLine => line !== null);

        if (convertedLines.length !== lines.length) {
          return null;
        }

        return (
          <Stack
            key={currency}
            style={{
              color: 'var(--color-text-secondary-black)',
              flexDirection: direction,
            }}
            gap={gap}
            alignItems="center"
          >
            <InlineText color="disabled">|</InlineText>
            <Stack
              style={{ flexDirection: tooltipDirection }}
              alignItems="center"
              gap="0.25rem"
              flexWrap="nowrap"
            >
              <ConversionTooltip conversionRate={priceConversion.rate} />
              <TransactionPriceTooltip
                price={{ amount: priceConversion.amount, currency, conversions: [] }}
                lines={convertedLines}
                size={size}
                variant="secondary"
              />
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

const TransactionPriceTooltip = ({
  price,
  lines,
  size,
  variant,
}: {
  price: TransactionPriceAmountWithConversions;
  lines: TransactionPriceLine[];
  size: 'x-small' | 'small' | 'medium';
  variant: 'primary' | 'secondary' | 'warning';
}) => {
  const paragraphSize = size === 'x-small' ? '300' : '200';

  return (
    <Tooltip
      variant="light"
      size="medium"
      hasOverlayArrow={false}
      textAlign="start"
      content={
        <table className={element('table')}>
          <tbody>
            {lines.map((line, index) => (
              <tr key={index} className={element('row')}>
                <td>
                  <InlineText size="300" color="secondary">
                    {line.label}
                  </InlineText>
                </td>
                <td>
                  <InlineText size="300" color="secondary">
                    {formatCurrency(line.amount, line.currency)}
                  </InlineText>
                </td>
              </tr>
            ))}
            <tr className={element('separator')}>
              <td colSpan={2} />
            </tr>
            <tr className={element('row', { 'first-row-of-section': true })}>
              <td>
                <InlineText size="200" weight="medium">
                  <Trans id="transaction-price-with-details.rows.total">Total</Trans>
                </InlineText>
              </td>
              <td>
                <InlineText size="200" weight="medium">
                  {formatCurrency(price.amount, price.currency)}
                </InlineText>
              </td>
            </tr>
          </tbody>
        </table>
      }
    >
      <Button variant="style-less">
        <Paragraph
          size={paragraphSize}
          weight={size === 'x-small' ? 'regular' : 'medium'}
          color={variant}
          className={element('trigger', { size })}
        >
          {formatCurrency(price.amount, price.currency)}
        </Paragraph>
      </Button>
    </Tooltip>
  );
};
